<script setup lang="ts">
/**
 * The bout screen — a full-viewport fight, not a panel popup.
 *
 * Layout: docks backdrop, hero standing LEFT facing right, opponent RIGHT
 * facing left, both full-body. The six numbered action cards run as a
 * VERTICAL rail down the left edge on desktop (owner call — the bottom row
 * read 1:1 like the mafinini reference) and as a 3x2 tray along the bottom
 * on phones. The HUD (Tekken-style HP bars left/right and the centre timer)
 * is ours — the mockup did not draw it.
 *
 * Drop-in for the old TacticalBattle popup: same props in, same `done` event
 * out, so CityMap / Arena / Dungeons mount it unchanged.
 */
import { ref, reactive, onMounted, onUnmounted, computed, watch } from 'vue';
import {
  startFight, startGarrison, startDefend, startHeist, startArena, fightRound, fightHeal, fightRestoreFury, fightAbandon, continueHeist,
  type FightView, type MobAction,
} from '../game/mobfight-api';
import { fightPose, fightCard, FIGHT_BG, mobArt, SHOP_ART, itemThumb, MYTHIC_ARCHETYPES, ELITE_TYPES, type FightPose } from '../game/art';
import { poseFit } from '../game/fight-pose-metrics';
import {
  TURN_SECONDS, resolveTurnDeadline, clearTurnDeadline, turnSecondsLeft,
} from '../game/turn-clock';
import { RARITY_HEX } from '../game/item-math';
import { classLabel, type HeroClass } from '../game/hero-api';
import * as sfx from '../game/sfx';
import * as music from '../game/music';
import { confirmAction } from '../game/confirm';
import cashIcon from '../assets/fimbulwake/icons/cash.webp';
import nerveIcon from '../assets/fimbulwake/icons/nerve.webp';
import runesIcon from '../assets/fimbulwake/icons/runes.webp';
import { t } from '../i18n';

const props = defineProps<{
  col?: number; row?: number; heroClass?: string;
  garrisonId?: string; defendId?: string; heistId?: string; arena?: boolean;
  /**
   * A bout the caller has ALREADY started. Pass this whenever you can: opening
   * the fight is what validates nerve, level and the spatial gate, so a caller
   * that starts it first can show "not enough nerve" in its own panel instead of
   * throwing the player onto a fight screen just to read an error. Without it we
   * fall back to starting on mount (which resumes a live session, never opens a
   * second one).
   */
  initial?: FightView | null;
}>();
const emit = defineEmits<{ (e: 'done', changed: boolean): void }>();

const view = ref<FightView | null>(null);
const error = ref('');
const busy = ref(false);
const changed = ref(false); // did the world change (a kill happened)

// ── Card → action. The number and title are baked into the card art, so this
// order is fixed and must match it: 1 STRIKE · 2 HEAVY · 3 GUARD · 4 FLEE ·
// 5 <class ability> · 6 PATCH UP (the heal endpoint, not a round action).
/** Vigor price for a card (heavy/flee/ability) — 0/undefined hides the badge.
 *  Guard reads 0 from the server (QA 15.08: defense is free), so its badge
 *  simply never shows; flee carries its flat retreat price. Item 9 (31.08):
 *  these prices are paid from the fight's own vigor pool (refilled only by
 *  the horn), never the hero's persistent fury. */
function vigorPrice(n: number): number {
  const av = view.value?.actionVigor;
  if (!av) return 0;
  return n === 2 ? av.heavy : n === 3 ? av.guard : n === 4 ? (av.flee ?? 0) : n === 5 ? av.ability : 0;
}

/** The card's real numbers THIS fight (QA 15.08: the tiny card text said
 *  nothing usable) — shown on the plate that appears when the card enlarges. */
function statLine(n: number): string | null {
  const m = view.value?.moves;
  if (!m) return null;
  switch (n) {
    case 1: return t('battle.stats.strike', { min: m.strike.min, max: m.strike.max, crit: m.strike.critPct });
    case 2: return t('battle.stats.heavy', { min: m.heavy.min, max: m.heavy.max, hit: m.heavy.hitPct, extra: m.heavy.extraTakenPct });
    case 3: return t('battle.stats.guard', { deal: 100 - m.guard.dealPct, take: 100 - m.guard.takenPct, block: m.guard.blockPct });
    case 4: return t('battle.stats.flee', { pct: m.flee.successPct });
    case 5: return view.value?.ability.desc ?? null;
    case 6: return t('battle.stats.heal', { pct: m.healPct });
    default: return null;
  }
}

const CARDS = [
  { n: 1, action: 'strike' as MobAction },
  { n: 2, action: 'heavy' as MobAction },
  { n: 3, action: 'guard' as MobAction },
  { n: 4, action: 'flee' as MobAction },
  { n: 5, action: 'ability' as MobAction },
  { n: 6, action: 'heal' as const },
];

// ── Poses ──
// Pacing. Poses are single stills, so a swap is a hard cut unless it is given
// time and a blend — at 620ms with an instant swap the fight read as a strobe.
// POSE_HOLD_MS is how long an action pose stays up; the crossfade itself is in
// the .fs-pose transition below. Both are deliberately unhurried: the bout is
// turn-based, nothing is waiting on them.
const POSE_HOLD_MS = 900;
const heroPose = ref<FightPose>('default');
const enemyPose = ref<FightPose>('default');
// One timer PER FIGHTER. A single shared timer meant the second flash of a round
// (hero swings, enemy reels) cancelled the first fighter's reset and left them
// stuck mid-pose.
const poseTimers: Record<'hero' | 'enemy', number> = { hero: 0, enemy: 0 };
function flash(who: 'hero' | 'enemy', pose: FightPose, ms = POSE_HOLD_MS) {
  const r = who === 'hero' ? heroPose : enemyPose;
  r.value = pose;
  window.clearTimeout(poseTimers[who]);
  poseTimers[who] = window.setTimeout(() => { if (r.value === pose) r.value = 'default'; }, ms);
}
const ACTION_POSE: Record<string, FightPose> = {
  strike: 'strike', heavy: 'heavy', guard: 'guard', flee: 'flee', ability: 'ability', heal: 'patch-up',
};

const heroClass = computed(() => view.value?.hero.class ?? props.heroClass);
const heroImg = computed(() => fightPose(heroClass.value, heroPose.value));
// The plate shows the in-game trade, never the raw engine enum ("MAGE" → "Fixer")
const heroClassLabel = computed(() => {
  const c = heroClass.value?.toUpperCase() as HeroClass | undefined;
  return (c && classLabel(c)) || t('battle.you');
});

/**
 * Warm every pose frame for a combatant the moment the fight opens. The stage
 * swaps keyed <img>s per pose; without this, the FIRST use of each pose on a
 * slow connection fetches mid-crossfade — the fighter blanks out and pops back
 * in, which reads as the sprite "jumping" between stances (mobile QA report).
 */
const ALL_POSES: FightPose[] = [
  'default', 'strike', 'heavy', 'guard', 'flee',
  'ability', 'patch-up', 'damage', 'damage-2', 'win', 'defeat',
];
const warmedPoseSets = new Set<string>();
function preloadPoses(cls?: string | null) {
  const key = cls?.toUpperCase();
  if (!key || warmedPoseSets.has(key)) return;
  warmedPoseSets.add(key);
  for (const pose of ALL_POSES) {
    const url = fightPose(key, pose);
    if (url) new Image().src = url;
  }
}
watch(
  () => [heroClass.value, view.value?.enemy.archetype] as const,
  ([hero, enemy]) => { preloadPoses(hero); preloadPoses(enemy); },
  { immediate: true },
);

/**
 * The opponent. `enemy.archetype` is the ONE key that covers every fight kind —
 * the Pit sends the bot's hero class lowercased (warrior|paladin|archer|mage),
 * and mobs, garrisons and heists all send a balance archetype
 * (thug|goon|bruiser|hitman|legbreaker|kingpin). `fight/` now holds a pose set
 * for all ten, so the opponent animates everywhere, not just in the Pit.
 * mobArt() stays as the fallback for anything without a pose set.
 */
/** Does this opponent have a pose set at all? Drives the fallback framing —
 *  a posed fighter stands on the ground like the hero, a portrait gets framed. */
const enemyIsFighter = computed(() => !!fightPose(view.value?.enemy.archetype, 'default'));
/** A mythic world boss stands a head taller than any fighter (owner 01.09:
 *  "the mythic sprite is much smaller" — it rendered at mob scale). */
const enemyIsBoss = computed(() => MYTHIC_ARCHETYPES.has(view.value?.enemy.archetype ?? ''));
/** A hold's elite beast sits between a mob and a world boss on the stage. */
const enemyIsElite = computed(() => (ELITE_TYPES as readonly string[]).includes(view.value?.enemy.archetype ?? ''));
const enemyImg = computed(() => {
  const v = view.value;
  if (!v) return undefined;
  return fightPose(v.enemy.archetype, enemyPose.value)
      ?? mobArt(v.enemy.type, v.enemy.archetype);
});

/**
 * Fighter-image sizing (QA: hero rendered bigger than the opponent, feet at
 * different heights; owner: fighters visibly GREW on damage/defeat). The pose
 * stills are independently generated cutouts — each bakes in its own
 * transparent margin — so a flat `height: 100%` scales that baked-in padding
 * right along with the character. `poseFit` (see fight-pose-metrics.ts) fixes
 * both reports at once: it renders every pose of a fighter at ONE constant
 * scale anchored to that class's default frame (so a pose swap never changes
 * the sprite's size — a crouched frame just reads shorter, as it should) and
 * slides each frame down by its own baked-in foot gap so both fighters stand
 * on the same ground line. Only meaningful for a posed fighter — the
 * portrait-fallback mob (.is-mob) keeps its own framing (height/object-fit
 * set in CSS), so its style binding is left undefined.
 */
const heroFit = computed(() => poseFit(heroClass.value, heroPose.value));
const enemyFit = computed(() => (enemyIsFighter.value ? poseFit(view.value?.enemy.archetype, enemyPose.value) : null));

/**
 * HP bars are driven by ANIMATED refs, not the raw view. A round arrives from the
 * server fully resolved (both fighters' final HP in one response), but showing
 * that at once reads as "I punched us both". So `play()` steps these two numbers
 * through the round — hero swings, enemy drops; enemy swings, hero drops — and the
 * bars (bound here) follow. They resync to the view whenever the view changes for
 * any other reason (mount, heal, resume, room advance).
 */
const animHeroHp = ref(0);
const animEnemyHp = ref(0);
const heroMaxHp = computed(() => view.value?.hero.maxHp ?? 1);
const enemyMaxHp = computed(() => view.value?.enemy.maxHp ?? 1);
const heroPct = computed(() => Math.max(0, Math.min(100, animHeroHp.value / heroMaxHp.value * 100)));
const enemyPct = computed(() => Math.max(0, Math.min(100, animEnemyHp.value / enemyMaxHp.value * 100)));
const heroHpText = computed(() => `${Math.max(0, Math.round(animHeroHp.value))}/${heroMaxHp.value}`);
const enemyHpText = computed(() => `${Math.max(0, Math.round(animEnemyHp.value))}/${enemyMaxHp.value}`);
/* one health scale across the whole game: aurora hale, ember hurt, blood critical */
const heroTone = computed(() =>
  heroPct.value > 50 ? 'var(--color-aurora)' : heroPct.value > 25 ? 'var(--color-ember)' : 'var(--color-error)');
const enemyTone = computed(() =>
  enemyPct.value > 50 ? '#8fb4cc' : enemyPct.value > 25 ? 'var(--color-ember)' : 'var(--color-error)');
const heroLow = computed(() => heroPct.value <= 25);
const enemyLow = computed(() => enemyPct.value <= 25);

/** Snap the bars to the view — used when HP changes outside a stepped round. */
function syncBars() {
  animHeroHp.value = view.value?.hero.hp ?? 0;
  animEnemyHp.value = view.value?.enemy.hp ?? 0;
}
const delay = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

const ended = computed(() => !!view.value && !view.value.active);

/** Heists are multi-room and garrisons are multi-defender: `wave` is "which of
 *  how many", and a heist parks on `awaitingNext` between rooms until the player
 *  presses on. Without this a heist dead-ends after the first room. */
const wave = computed(() => view.value?.wave ?? null);

/**
 * A garrison is several defenders in a row, and when one fell the next simply
 * replaced the sprite mid-frame — "second, third user just appears" (QA
 * 86cb1qvpt). This holds the hand-over: the fallen one stays down, a card names
 * who steps over them, and only then does the new fighter take the ground.
 */
const relieving = ref<{ fallen: string; next: string; index: number; total: number } | null>(null);
const awaitingNext = computed(() => !!view.value?.heist?.awaitingNext);
const banked = computed(() => view.value?.heist?.bankedCash ?? 0);

// ── HOLMGANG PACING ────────────────────────────────────────────────────────
// The round already plays in two steps, but the whole exchange was over in
// about a second, so a sworn opponent answered like a reflex and the duel read
// as a mob fight (QA 86cb1qvpt: "it seems that this is just NPC mob"). The
// server keeps resolving both halves at once — that stays authoritative — the
// RING now paces it as two turns: a clock you must answer on, and a foe that
// takes a moment to choose before it swings.
//
// Arena only. The same clock on a PvE mob would just tax grinding.
const THINK_MIN_MS = 1400;
const THINK_MAX_MS = 3200;
const rollThinkMs = () => THINK_MIN_MS + Math.floor(Math.random() * (THINK_MAX_MS - THINK_MIN_MS + 1));

const isDuel = computed(() => view.value?.kind === 'arena');
/** True only while the ring is genuinely waiting on the player. */
const myTurn = computed(() => !!(isDuel.value && view.value?.active && !busy.value && !ended.value && !awaitingNext.value));
const turnLeft = ref(TURN_SECONDS);
const foeThinking = ref(false);
let turnTimer: number | null = null;
let turnDeadline = 0;

function stopTurnClock() {
  if (turnTimer !== null) { window.clearInterval(turnTimer); turnTimer = null; }
}

/** One reading of the clock. Returns true once the turn has lapsed. */
function readTurnClock(): boolean {
  const left = turnSecondsLeft(turnDeadline);
  turnLeft.value = left;
  if (left > 0) return false;
  // Out of time — the arm swings anyway. Stalling would just park a live
  // session until its TTL with the opponent unanswered.
  stopTurnClock();
  pop('hero', 'TOO SLOW', 'miss');
  void play({ n: 1, action: 'strike' });
  return true;
}

/**
 * Take up the turn. The deadline is ABSOLUTE and parked in sessionStorage, so a
 * page refresh resumes the same turn instead of granting a fresh 15s (QA
 * 86cb8z0xk) — and a turn that ran out while the tab was away lapses the moment
 * the player comes back. Ticking four times a second is for the bar, not the
 * rule: the number is always derived from the deadline, never decremented.
 */
function startTurnClock() {
  stopTurnClock();
  turnDeadline = resolveTurnDeadline().deadline;
  if (readTurnClock()) return; // already lapsed — nothing left to tick
  turnTimer = window.setInterval(readTurnClock, 250);
}
watch(myTurn, (on) => { if (on) startTurnClock(); else stopTurnClock(); });
onUnmounted(stopTurnClock);

/* The hand is a bottom row again (owner's call — the left rail read badly and
 * covered the fighters). The rail's whole measureRail/ResizeObserver machinery
 * went with it: a bottom row sizes each card as a SHARE OF THE WIDTH in plain
 * CSS (see .fs-card), so the old failure modes — clipped sixth card, sideways
 * overflow on narrow windows — are impossible by construction, no JS needed. */

async function pressOn() {
  if (busy.value) return;
  // The calling card, same rail as the City Map's pre-fight confirm
  // (CU-86cbc65ny item 5: every fight is confirmed with a pop-up — raids too).
  // Declining is free: it just returns to the room-clear panel, where the take
  // can still be banked.
  const nextName = view.value?.heist?.nextEnemyName;
  const go = await confirmAction({
    kicker: t('dungeons.confirm.kicker'),
    title: nextName ? t('battle.nextUp', { name: nextName }) : t('battle.wayOpen'),
    rows: banked.value > 0
      ? [{ label: t('battle.interRoom.bankedSoFar'), value: banked.value.toLocaleString(), icon: cashIcon, tone: 'gold' }]
      : undefined,
    note: t('battle.roomConfirm.note'),
    confirmLabel: t('battle.pressOn'),
    cancelLabel: t('battle.roomConfirm.stay'),
  });
  if (!go || busy.value) return;
  busy.value = true;
  try {
    const prevRoom = view.value?.wave?.index ?? 0;
    view.value = await continueHeist();
    syncBars();
    music.setScene('fight'); // pushing deeper re-raises the drums (idempotent mid-run)
    if ((view.value.wave?.index ?? 0) > prevRoom) sfx.clash();
    heroPose.value = 'default';
    enemyPose.value = 'default';
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    busy.value = false;
  }
}

// ── Cosmetic fight timer. Counts elapsed time and NOTHING else: it never ends
// the bout, never touches the outcome, never calls the server (explicit design
// decision — a Tekken-style timeout was rejected).
//
// The START is persisted for the tab's lifetime, because the clock used to
// restart at 00:00 every time this component mounted — so leaving a bout and
// coming back read as "the fight restarted, the timer reset" (QA 2026-08-17)
// even though the server session was the same one. Cleared when a bout ends, so
// the next fight starts from zero.
const CLOCK_KEY = 'fw:fight-clock-start';
function clockStart(): number {
  try {
    const stored = Number(sessionStorage.getItem(CLOCK_KEY));
    if (Number.isFinite(stored) && stored > 0) return stored;
    const now = Date.now();
    sessionStorage.setItem(CLOCK_KEY, String(now));
    return now;
  } catch { return Date.now(); } // private mode / storage disabled
}
function clearClock() {
  try { sessionStorage.removeItem(CLOCK_KEY); } catch { /* nothing to clear */ }
}
const seconds = ref(0);
let tick = 0;
const clock = computed(() => `${String(Math.floor(seconds.value / 60)).padStart(2, '0')}:${String(seconds.value % 60).padStart(2, '0')}`);

// ── The session's REAL clock: the server keeps an idle bout alive for 5 minutes
// (refreshed on every action). Before this, letting it lapse dead-ended the
// screen — every move answered "No active fight" and even fleeing was
// impossible. Now the deadline is visible, and lapsing closes the bout cleanly.
const expiresIn = ref(0);
const cold = ref(false);
const expiryWarn = computed(() => !cold.value && !ended.value && expiresIn.value > 0 && expiresIn.value <= 60);
const expiryClock = computed(() => `${Math.floor(expiresIn.value / 60)}:${String(expiresIn.value % 60).padStart(2, '0')}`);
watch(view, (v) => { if (v?.expiresInSec !== undefined) expiresIn.value = v.expiresInSec; });
/** A server "that fight is gone" reply, on any action → cold end, not an error. */
function goCold() {
  clearTurnDeadline();
  cold.value = true;
  error.value = '';
  heroPose.value = 'default';
  enemyPose.value = 'default';
}

// ── Floating damage popups. 2s on screen — at 1.1s the number was gone before
// the eye got to it (player feedback), and the round pacing gives each popup
// its own beat anyway so longer never overlaps the next one. ──
const POP_MS = 2000;
let pid = 0;
const popups = reactive<{ id: number; side: 'hero' | 'enemy'; text: string; kind: string }[]>([]);
function pop(side: 'hero' | 'enemy', text: string, kind = 'dmg') {
  const id = ++pid;
  popups.push({ id, side, text, kind });
  window.setTimeout(() => { const i = popups.findIndex((p) => p.id === id); if (i >= 0) popups.splice(i, 1); }, POP_MS);
}

function cardEnabled(n: number): boolean {
  const v = view.value;
  // between heist rooms there is nobody to swing at — the only move is "press on"
  if (!v || busy.value || ended.value || awaitingNext.value || cold.value) return false;
  if (n === 5) return v.ability.ready && affordable(n);
  // patch-up: needs a heal left, room to heal, and the cash for it. healAffordable
  // already folds all three together (see the view) — greying the card beats
  // clicking into an "Insufficient balance" error.
  if (n === 6) return v.healAffordable ?? v.healsLeft > 0;
  return affordable(n);
}

/** Vigor gate for a card. Flee (4) is NEVER gated — a retreat is always open
 *  (it spends what's there, clamped server-side); heavy and the ability grey
 *  out on a dry pool — the free strike and guard still always play. */
function affordable(n: number): boolean {
  if (n === 4) return true;
  const vg = view.value?.vigor;
  if (!vg) return true;
  return vigorPrice(n) <= vg.current;
}

/** Once-per-fight rune fury refill (QA 15.08). Not a card — the six card
 *  faces are baked art — so it hangs off the hero's HP plate instead. */
async function pullHorn() {
  const fr = view.value?.furyRestore;
  if (!fr || fr.used || !fr.affordable || busy.value) return;
  clearTurnDeadline(); // the horn re-arms the clock, as it always has
  busy.value = true;
  error.value = '';
  try {
    view.value = await fightRestoreFury();
    sfx.heal();
    pop('hero', t('battle.horn'), 'heal');
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    busy.value = false;
  }
}

/** The card the player just committed — the round's SFX are chosen from it
 *  (the server reports what happened, not which weapon swung). */
let lastAction: MobAction | 'heal' = 'strike';

async function play(card: { n: number; action: MobAction | 'heal' }) {
  if (!cardEnabled(card.n)) return;
  // This turn is answered, so its deadline is spent — drop it and the next turn
  // arms a fresh one. Only a reload mid-turn finds a deadline still standing.
  clearTurnDeadline();
  lastAction = card.action;
  busy.value = true;
  error.value = '';
  try {
    if (card.action === 'heal') {
      const res = await fightHeal();
      view.value = res;
      flash('hero', 'patch-up');
      // the heal is a single, non-combat event — snap the hero bar up to it
      animHeroHp.value = res.hero.hp;
      sfx.heal();
      pop('hero', t('battle.patched'), 'heal');
      return;
    }

    const prevWave = view.value?.wave?.index ?? 0;
    const fallen = view.value?.enemy.name ?? '';
    flash('hero', ACTION_POSE[card.action] ?? 'strike');
    const res = await fightRound(card.action); // fully-resolved round
    // a completed non-arena bout persisted hero HP — the world/profile changed
    if (res.result) changed.value = true;
    await animateRound(res);
    // A defender fell and the next steps up — play the relief BEFORE committing
    // the view, so the fallen one is still the figure on the ground for it.
    if (res.wave && res.wave.index > prevWave && !res.result) {
      window.clearTimeout(poseTimers.enemy);
      enemyPose.value = 'defeat';
      await delay(700);
      relieving.value = { fallen, next: res.enemy.name, index: res.wave.index, total: res.wave.total };
      sfx.clash();
      await delay(1600);
      relieving.value = null;
      enemyPose.value = 'default';
    }
    // commit the authoritative state only after the beat-by-beat has played out
    view.value = res;
    syncBars();
  } catch (e) {
    const msg = (e as Error).message;
    // idle TTL lapsed server-side → the bout is simply over, not an error state
    if (/no active fight/i.test(msg)) goCold();
    else error.value = msg;
  } finally {
    busy.value = false;
  }
}

/**
 * Walk one round's outcome through the two bars so cause and effect read in
 * order: (bleed) → hero acts, enemy reels → enemy retaliates, hero reels. The
 * server already resolved everything; we only pace the presentation, driving the
 * anim refs off the outcome deltas and letting the bar's own CSS tween each drop.
 */
async function animateRound(res: FightView) {
  const o = res.outcome;
  if (!o) return;
  const before = view.value; // pre-round HP baseline
  animHeroHp.value = before?.hero.hp ?? res.hero.hp;
  animEnemyHp.value = before?.enemy.hp ?? res.enemy.hp;

  // MAGE bleed ticks at the top of the round, before anyone swings
  if (o.bleedDmg > 0) {
    await delay(200);
    animEnemyHp.value = Math.max(0, animEnemyHp.value - o.bleedDmg);
    pop('enemy', `-${o.bleedDmg}`, 'dmg');
    flash('enemy', 'damage');
    await delay(420);
  }

  // STEP A — the hero's blow lands (or misses)
  await delay(300); // let the swing pose read before the hit
  if (o.heroHeal > 0) { // PALADIN Second Wind self-patch
    animHeroHp.value = Math.min(heroMaxHp.value, animHeroHp.value + o.heroHeal);
    pop('hero', `+${o.heroHeal}`, 'heal');
  }
  if (o.heroDmg > 0) {
    animEnemyHp.value = Math.max(0, animEnemyHp.value - o.heroDmg);
    pop('enemy', `-${o.heroDmg}`, o.heroEvent === 'crit' ? 'crit' : 'dmg');
    flash('enemy', 'damage');
    // the weapon that swung, then the weight of what landed
    sfx.attackSfx(props.heroClass, o.heroEvent === 'ability' ? 'ability' : lastAction);
    if (o.heroEvent === 'crit' || lastAction === 'heavy') sfx.heavy(); else sfx.hit();
  } else if (o.heroEvent === 'miss') {
    pop('enemy', 'MISS', 'miss');
  }

  // A FAILED break-away, called out on its own. This used to sit at the end of
  // the `mobDmg / block / fleeFailed` else-chain — but a failed flee ALWAYS eats
  // the foe's swing, so the damage branch always won and the player saw nothing
  // but a normal hit: the FLEE card read as "did nothing, skipped my turn"
  // (QA 2026-08-17). Now the attempt is visible before the punishment lands.
  if (o.fleeFailed) {
    heroPose.value = 'flee';
    pop('hero', t('battle.fleeFailed'), 'miss');
    sfx.block();
    await delay(420);
  }

  // The foe's TURN. In a duel it deliberates first — a randomised beat, so the
  // counter reads as a decision rather than a reflex, and never the same one
  // twice.
  if (isDuel.value && !o.fled && (o.mobDmg > 0 || o.mobEvent === 'block')) {
    foeThinking.value = true;
    await delay(rollThinkMs());
    foeThinking.value = false;
  }

  // STEP B — the enemy answers, in its own beat, only if it can
  if (o.mobDmg > 0) {
    await delay(560);
    flash('enemy', 'strike');
    await delay(260);
    animHeroHp.value = Math.max(0, animHeroHp.value - o.mobDmg);
    pop('hero', `-${o.mobDmg}`, o.mobEvent === 'crit' ? 'crit' : 'dmg');
    flash('hero', 'damage');
    sfx.hit();
  } else if (o.mobEvent === 'block') {
    await delay(300);
    sfx.block();
    pop('hero', t('battle.block'), 'miss');
  }

  await delay(320); // hold on the final frame before the outcome panel
}

// End-of-bout poses: the winner celebrates, the loser goes down. Watched rather
// than set at each call site so a bout that ends on ANY path — a landed hit, a
// failed flee, the opponent's swing — settles the same way. A fled bout is a
// no-contest: nobody poses.
watch(view, (v) => {
  if (!v || v.active || !v.result) return;
  // Settled — no turn is owed, so no deadline should outlive the bout and greet
  // the next duel already lapsed.
  clearTurnDeadline();
  window.clearTimeout(poseTimers.hero);
  window.clearTimeout(poseTimers.enemy);
  if (v.fled) {
    // a walk-out is a no-contest — hold the hero mid-escape on the outcome
    // screen instead of snapping back to idle (the pose timer would).
    heroPose.value = 'flee';
    enemyPose.value = 'default';
    music.fled();
    music.setScene('lobby'); // the sting carries the exit; the wind returns under it
    return;
  }
  const won = v.result === 'VICTORY';
  if (won) music.victory(); else music.defeat();
  // Hand the room back to the hold's theme the moment the bout settles —
  // waiting for unmount left war drums running the whole outcome screen,
  // which read as "the music never switches back" (CU-86cb58yuh item 8).
  music.setScene('lobby');
  heroPose.value = won ? 'win' : 'defeat';
  enemyPose.value = won ? 'defeat' : 'win';
});

function leave() {
  // The bout is over (settled, fled or gone cold) — the next one starts its
  // clock from zero rather than inheriting this one's start.
  clearClock();
  clearTurnDeadline();
  emit('done', changed.value);
}

/**
 * Walk out. For MOB/GARRISON/HEIST this is unchanged — an immediate, no-
 * questions bail. For an ARENA bout (the Pit), walking out mid-fight is a
 * FORFEIT — it settles exactly like losing the last round (rating drop +
 * stake burn) — so we confirm first (this can't be undone) and then show the
 * settled LOSS outcome instead of just vanishing.
 */
async function bail() {
  if (busy.value) return;
  if (!ended.value) {
    if (view.value?.kind === 'arena') {
      const go = await confirmAction({
        kicker: t('battle.confirm.kicker'),
        title: t('battle.confirm.walkOutTitle'),
        note: t('battle.confirm.walkOutNote'),
        confirmLabel: t('battle.confirm.walkOutConfirm'),
        cancelLabel: t('battle.confirm.keepFighting'),
        danger: true,
      });
      if (!go) return;
    }
    busy.value = true;
    try {
      const res = await fightAbandon();
      if (res.result) {
        // Arena forfeit — a settled view came back; show the loss outcome
        // (the end-of-bout panel below) instead of leaving immediately. The
        // watch(view, ...) pose effect and the "Leave"/top-bar button both
        // already handle an ended view correctly from here.
        view.value = res as FightView;
        syncBars();
        changed.value = true; // rating/gold changed — the profile header should refresh
        return;
      }
    } catch { /* ignore */ } finally {
      busy.value = false;
    }
  }
  leave();
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') { void bail(); return; }
  const n = Number(e.key);
  if (n >= 1 && n <= 6) { const c = CARDS.find((x) => x.n === n); if (c) void play(c); }
}

onMounted(async () => {
  if (props.initial) {
    // Caller already started (and therefore validated) the bout — nothing to do.
    view.value = props.initial;
    syncBars();
  } else {
    try {
      // Fallback for callers that mount us cold. NOTE: every start-* resumes an
      // already-live session rather than opening a second one — starting a fresh
      // bout here would charge the player nerve twice.
      view.value = props.garrisonId
        ? await startGarrison(props.garrisonId)
        : props.defendId
          ? await startDefend(props.defendId)
          : props.heistId
          ? await startHeist(props.heistId)
          : props.arena
            ? await startArena()
            : await startFight(props.col ?? 0, props.row ?? 0);
      syncBars();
    } catch (e) {
      error.value = (e as Error).message;
    }
  }
  // the bout gets its own bed; leaving walks back to the hold's wind
  music.setScene('fight');
  // Elapsed is derived from the persisted start, so a remount resumes the count.
  const startedAt = clockStart();
  seconds.value = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
  tick = window.setInterval(() => {
    if (ended.value || cold.value) return;
    seconds.value = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
    if (expiresIn.value > 0) {
      expiresIn.value -= 1;
      if (expiresIn.value === 0) goCold(); // lapsed while we watched
    }
  }, 1000);
  window.addEventListener('keydown', onKeydown);
});
onUnmounted(() => {
  music.setScene('lobby');
  window.clearInterval(tick);
  window.clearTimeout(poseTimers.hero);
  window.clearTimeout(poseTimers.enemy);
  window.removeEventListener('keydown', onKeydown);
});

</script>

<template>
  <div class="fs-root" :style="{ backgroundImage: `url(${FIGHT_BG})` }">
    <div class="fs-scrim"></div>

    <!-- ── HUD: HP bars left/right + the cosmetic timer between them ── -->
    <header class="fs-hud">
      <div class="fs-bar fs-bar-home">
        <span class="fs-name">{{ heroClassLabel }}</span>
        <!-- THE LAST ART-DECO OBJECT IN THE BUILD is gone from here: a 1414x140
             raster whose own comment in art.ts called it "ONE ornate art-deco
             frame". Phase D only hue-shifted it. It is shield boards in an iron
             channel now, the same gauge as the hero card, the raid party and the
             ward chests — and it deletes a raster dependency. -->
        <div class="fs-track">
          <div class="bar-shield" :class="{ 'is-splintered': heroLow }"
               :style="{ '--sh-pct': heroPct, '--sh-tone': heroTone, '--sh-boards': 14 }">
            <div class="bar-shield-fill"></div>
          </div>
          <span class="fs-hp">{{ heroHpText }}</span>
        </div>
        <!-- VIGOR — the fight's own energy (item 9, 31.08): the big moves
             spend it, the horn refills it, and the hero's persistent fury is
             never touched. -->
        <!-- Owner 01.09: not a text chip — a small HP-style shield-board bar,
             one board per vigor point, aurora-toned to match its costs. -->
        <div v-if="view?.vigor && !ended" class="fs-vigor" :title="t('battle.vigorTitle')">
          <svg class="fs-bolt" viewBox="0 0 24 24" aria-hidden="true"><path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" fill="currentColor" /></svg>
          <div
            class="bar-shield fs-vigor-bar"
            :style="{ '--sh-pct': (view.vigor.current / Math.max(1, view.vigor.max)) * 100, '--sh-tone': 'var(--color-aurora)', '--sh-boards': view.vigor.max }"
          >
            <div class="bar-shield-fill"></div>
          </div>
          <span class="fs-vigor-n">{{ view.vigor.current }}/{{ view.vigor.max }}</span>
        </div>
        <!-- the Horn of Mead — one rune-paid VIGOR refill per fight (QA 15.08;
             re-pointed at the vigor pool by item 9). Hangs off the hero's
             plate: the six cards are baked art, no 7th slot. -->
        <button
          v-if="view?.furyRestore && !ended"
          class="fs-horn"
          :class="{ 'is-spent': view.furyRestore.used }"
          :disabled="busy || view.furyRestore.used || !view.furyRestore.affordable"
          @click="pullHorn"
        >
          <svg class="fs-bolt" viewBox="0 0 24 24" aria-hidden="true"><path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" fill="currentColor" /></svg>
          <template v-if="view.furyRestore.used">{{ t('battle.hornUsed') }}</template>
          <template v-else>{{ t('battle.horn') }} · <img :src="runesIcon" :alt="t('battle.runes')" class="fs-badge-cash" />{{ view.furyRestore.cost }}</template>
        </button>
      </div>

      <div class="fs-clock" role="timer" :aria-label="t('battle.fightTime')">
        <span class="fs-clock-t">{{ clock }}</span>
        <span class="fs-clock-r">{{ t('battle.round', { n: view?.round ?? 1 }) }}</span>
        <!-- the session's real deadline, surfaced only when it gets close -->
        <span v-if="expiryWarn" class="fs-clock-x">{{ t('battle.goesCold', { time: expiryClock }) }}</span>
        <!-- heists run several rooms, garrisons several defenders -->
        <span v-if="wave" class="fs-clock-w">
          {{ view?.kind === 'heist' ? t('battle.room', { current: wave.index, total: wave.total }) : t('battle.defender', { current: wave.index, total: wave.total }) }}
        </span>
        <span v-if="banked > 0" class="fs-clock-b">{{ t('battle.banked', { amount: banked.toLocaleString() }) }}</span>
        <!-- the duel's shot clock — the engine force-plays on timeout, so the
             player must SEE the countdown that is about to punish them -->
        <span v-if="isDuel && myTurn" class="fs-turn" :class="{ 'is-late': turnLeft <= 5 }">
          <span class="fs-turn-lbl">{{ t('battle.yourMove') }}</span>
          <b class="fs-turn-n tabular-nums">{{ turnLeft }}s</b>
          <span class="fs-turn-track" aria-hidden="true">
            <i :style="{ width: (turnLeft / TURN_SECONDS * 100) + '%' }"></i>
          </span>
        </span>
      </div>

      <div class="fs-bar fs-bar-guest">
        <span class="fs-name fs-name-r">{{ view?.enemy.name ?? '—' }}</span>
        <div class="fs-track is-guest">
          <div class="bar-shield" :class="{ 'is-splintered': enemyLow }"
               :style="{ '--sh-pct': enemyPct, '--sh-tone': enemyTone, '--sh-boards': 14 }">
            <div class="bar-shield-fill"></div>
          </div>
          <span class="fs-hp">{{ enemyHpText }}</span>
        </div>
      </div>

      <button class="fs-bail btn-forge btn-forge--carved btn-forge--sm" :disabled="busy" @click="bail">
        {{ ended ? t('battle.leave') : t('battle.walkOut') }}
      </button>
    </header>

    <!-- ── The two fighters ── -->
    <div class="fs-stage">
      <div class="fs-fighter fs-hero">
        <Transition name="fs-pose">
          <img
            v-if="heroImg" :key="heroPose" :src="heroImg" :alt="heroClassLabel"
            :style="{ height: heroFit.height, transform: `translateY(${heroFit.translateY}%)` }"
          />
        </Transition>
        <div class="fs-pops">
          <span v-for="p in popups.filter((x) => x.side === 'hero')" :key="p.id" :class="['fs-pop', `is-${p.kind}`]">{{ p.text }}</span>
        </div>
      </div>

      <div class="fs-fighter fs-enemy" :class="{ 'is-mob': !enemyIsFighter, 'is-boss': enemyIsBoss, 'is-elite': enemyIsElite }">
        <Transition name="fs-pose">
          <img
            v-if="enemyImg" :key="enemyPose" :src="enemyImg" :alt="view?.enemy.name"
            :style="enemyFit ? { height: enemyFit.height, transform: `scaleX(-1) translateY(${enemyFit.translateY}%)` } : undefined"
          />
        </Transition>
        <div class="fs-pops">
          <span v-for="p in popups.filter((x) => x.side === 'enemy')" :key="p.id" :class="['fs-pop', `is-${p.kind}`]">{{ p.text }}</span>
        </div>
        <!-- it is the foe's turn, and it is choosing -->
        <Transition name="fs-pose">
          <div v-if="foeThinking" class="fs-think">
            <span>{{ t('battle.weighingStrike') }}</span>
            <span class="fs-think-dots" aria-hidden="true"><i></i><i></i><i></i></span>
          </div>
        </Transition>
      </div>
    </div>

    <!-- the line holds: one defender down, the next steps over them -->
    <Transition name="fs-relief">
      <div v-if="relieving" class="fs-relief" role="status">
        <div class="fs-relief-kicker">{{ relieving.fallen }} goes down</div>
        <div class="fs-relief-name">{{ relieving.next }} steps over the body</div>
        <div class="fs-relief-count">Defender {{ relieving.index }} of {{ relieving.total }}</div>
      </div>
    </Transition>

    <p v-if="error" class="fs-error">{{ error }}</p>

    <!-- ── Outcome ── -->
    <div v-if="ended" class="fs-outcome bound-frame">
                  <h2>{{ view?.fled ? t('battle.outcome.walkedOut') : view?.result === 'VICTORY' ? t('battle.outcome.youWin') : t('battle.outcome.beaten') }}</h2>

      <div v-if="view?.rewards?.leveledUp" class="fs-oc-levelup">
        <span>&#5869;</span> {{ t('battle.levelUp', { level: view.rewards.level }) }} <span>&#5869;</span>
      </div>

      <!-- the Pit settles in ELO + cash stakes; without this the bout has no point -->
      <div v-if="view?.kind === 'arena' && view.arena" class="fs-plates">
        <div class="fs-plate">
          <span class="fs-plate-art fs-badge-elo" :class="view.arena.won ? 'is-win' : 'is-loss'">{{ view.arena.won ? '▲' : '▼' }}</span>
          <div>
            <div class="fs-plate-label">{{ t('battle.ratingLabel') }}</div>
            <div class="fs-plate-num" :class="{ 'is-loss': !view.arena.won }">
              {{ view.arena.ratingChange >= 0 ? '+' : '−' }}{{ Math.abs(view.arena.ratingChange) }} &rarr; {{ view.arena.rating }}
            </div>
            <div class="fs-plate-sub">{{ view.arena.title }}</div>
          </div>
        </div>
        <div class="fs-plate">
          <div>
            <div class="fs-plate-label">{{ t('battle.recordLabel') }}</div>
            <div class="fs-plate-num">{{ view.arena.wins }}W&ndash;{{ view.arena.losses }}L</div>
            <div v-if="view.arena.streak !== 0" class="fs-plate-sub">
              {{ view.arena.streak > 0 ? t('battle.winStreakN', { n: view.arena.streak }) : t('battle.lossSkidN', { n: Math.abs(view.arena.streak) }) }}
            </div>
          </div>
        </div>
        <!-- The saga standing the bout moved. The outcome used to report the ELO
             rating and nothing else, so the flat standing stake the pre-fight
             terms quote was never confirmed anywhere — and players read the
             rating's variable swing as the broken promise (QA 86cb8zdku). -->
        <div v-if="view.arena.rankPointsChange !== 0" class="fs-plate">
          <div>
            <div class="fs-plate-label">{{ t('battle.standingLabel') }}</div>
            <div class="fs-plate-num" :class="{ 'is-loss': view.arena.rankPointsChange < 0 }">
              {{ view.arena.rankPointsChange > 0 ? '+' : '−' }}{{ Math.abs(view.arena.rankPointsChange) }}
            </div>
          </div>
        </div>
        <!-- Silver settles under the SILVER coin, not the cash-rush sack — the
             boost's art on a payout read as the wrong currency (QA 18.08 item 20). -->
        <div v-if="view.arena.goldWon > 0 || view.arena.goldLost > 0" class="fs-plate">
          <img :src="cashIcon" alt="" class="fs-plate-art" />
          <div>
            <div class="fs-plate-label">{{ view.arena.won ? t('battle.liftedOff') : t('battle.paidOut') }}</div>
            <div class="fs-plate-num" :class="{ 'is-loss': !view.arena.won }">
              {{ view.arena.won ? `+${view.arena.goldWon.toLocaleString()}` : `−${view.arena.goldLost.toLocaleString()}` }}
            </div>
          </div>
        </div>
      </div>

      <!-- the take -->
      <div v-if="view?.rewards || (view?.kind === 'heist' && view.heist)" class="fs-plates">
        <div v-if="view?.rewards && view.rewards.cash > 0" class="fs-plate">
          <img :src="cashIcon" alt="" class="fs-plate-art" />
          <div>
            <div class="fs-plate-label">{{ t('battle.theTake') }}</div>
            <div class="fs-plate-num">+{{ view.rewards.cash.toLocaleString() }}</div>
          </div>
        </div>
        <div v-if="view?.rewards && view.rewards.xp > 0" class="fs-plate">
          <img :src="SHOP_ART.xp_booster" alt="" class="fs-plate-art" />
          <div>
            <div class="fs-plate-label">{{ t('battle.reputation') }}</div>
            <div class="fs-plate-num is-xp">+{{ view.rewards.xp.toLocaleString() }} XP</div>
          </div>
        </div>
        <div v-if="view?.kind === 'heist' && view.heist" class="fs-plate">
          <img :src="SHOP_ART.chest_pull" alt="" class="fs-plate-art" />
          <div>
            <div class="fs-plate-label">{{ t('battle.bankedFromJob') }}</div>
            <div class="fs-plate-num">{{ view.heist.bankedCash.toLocaleString() }}</div>
          </div>
        </div>
      </div>

      <!-- loot: the real piece, framed in its rarity -->
      <div v-if="view?.loot" class="fs-loot" :style="{ '--rar': RARITY_HEX[view.loot.rarity] }">
        <div class="fs-loot-art"><img :src="itemThumb(view.loot.type, view.loot.rarity, view.loot.name)" :alt="view.loot.name" /></div>
        <div>
          <div class="fs-plate-label">{{ t('battle.liftedOffBody') }}</div>
          <div class="fs-loot-name">{{ view.loot.name }}</div>
          <span class="fs-rarity">{{ view.loot.rarity.toLowerCase() }}</span>
        </div>
      </div>

      <button class="btn-forge btn-forge--rune btn-forge--lg" @click="leave">
        {{ view?.result === 'VICTORY' ? t('battle.collectAndGo') : t('battle.walkItOff') }}
      </button>
    </div>

    <!-- The session lapsed while the player was away — a clean no-contest end
         instead of every card answering "No active fight". -->
    <div v-else-if="cold" class="fs-outcome bound-frame">
                  <h2>{{ t('battle.cold.title') }}</h2>
      <p class="fs-next">{{ t('battle.cold.desc') }}</p>
      <button class="btn-forge btn-forge--rune btn-forge--lg" @click="leave">{{ t('battle.cold.walkAway') }}</button>
    </div>

    <!-- Between heist rooms: the only available move. -->
    <div v-else-if="awaitingNext" class="fs-outcome bound-frame">
                  <h2>{{ t('battle.roomClear') }}</h2>
      <!-- THIS room's cut, plated with the silver icon (QA: "silver icon after
           each raid level") — the payout used to be a bare "· 312 banked" text
           tail, indistinguishable from flavour copy. -->
      <div v-if="(view?.heist?.roomCash ?? 0) > 0" class="fs-plates">
        <div class="fs-plate">
          <img :src="cashIcon" alt="" class="fs-plate-art" />
          <div>
            <div class="fs-plate-label">{{ t('battle.roomCut') }}</div>
            <div class="fs-plate-num">+{{ view!.heist!.roomCash!.toLocaleString() }}</div>
          </div>
        </div>
      </div>
      <p class="fs-next">
        {{ view?.heist?.nextEnemyName ? t('battle.nextUp', { name: view.heist.nextEnemyName }) : t('battle.wayOpen') }}
        <span v-if="banked > 0"> · {{ banked.toLocaleString() }} {{ t('battle.bankedLabel') }}</span>
      </p>
      <!-- Won it, but an earlier attempt already stripped this hall — say so
           rather than letting the tally silently not move. -->
      <p v-if="view?.heist?.reclaimed" class="fs-next fs-next-dim">
        This hall was already stripped — no silver left to take.
      </p>
      <div class="fs-next-row">
        <button class="btn-forge btn-forge--rune" :disabled="busy" @click="pressOn">{{ t('battle.pressOn') }}</button>
        <button class="btn-forge btn-forge--carved" :disabled="busy" @click="bail">{{ t('battle.takeTheTake') }}</button>
      </div>
    </div>

    <!-- ── The six action cards. Art carries its own number + title.
         Desktop: a vertical rail down the left edge, beside the hero.
         Phone: a 3x2 tray along the bottom (see the media query). ── -->
    <footer v-else-if="!awaitingNext" class="fs-cards">
      <button
        v-for="c in CARDS" :key="c.n"
        class="fs-card"
        :class="{ 'is-off': !cardEnabled(c.n) }"
        :disabled="!cardEnabled(c.n)"
        @click="play(c)"
      >
        <img :src="fightCard(heroClass, c.n)" :alt="`Action ${c.n}`" />
        <!-- the move's real numbers, revealed as the card enlarges (QA 15.08) -->
        <span v-if="statLine(c.n)" class="fs-card-stats">{{ statLine(c.n) }}</span>
        <span v-if="c.n === 5 && view && !view.ability.ready" class="fs-badge">{{ t('battle.cooling') }}</span>
        <!-- item 9: the big moves spend VIGOR (the fight's own energy, never
             the hero's fury) — say the price on the card itself -->
        <span v-else-if="vigorPrice(c.n)" class="fs-badge" :class="{ 'is-broke': !affordable(c.n) }">
          <svg class="fs-bolt" viewBox="0 0 24 24" :aria-label="t('battle.vigorCost')" role="img"><path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" fill="currentColor" /></svg>{{ vigorPrice(c.n) }}
        </span>
        <!-- rune-priced since QA 15.08 — the coin face must say what it charges -->
        <span v-else-if="c.n === 6 && view" class="fs-badge" :class="{ 'is-broke': view.healAffordable === false && view.healsLeft > 0 }">
          {{ t('battle.left', { count: view.healsLeft }) }} · <img :src="runesIcon" :alt="t('battle.runes')" class="fs-badge-cash" />{{ view.healCost ?? 50 }}
        </span>
        <span class="fs-key">{{ c.n }}</span>
      </button>
    </footer>
  </div>
</template>

<style scoped>
.fs-root {
  position: fixed; inset: 0; z-index: 60;
  display: flex; flex-direction: column;
  background-size: cover; background-position: center;
  overflow: hidden;
  /* The card rail as ONE solved system. The old knob was
       clamp(64px, calc((100vh - 150px) / 6), 150px)
     where 150px stood in for "HUD plus bottom edge" — but it counted neither
     the five inter-card gaps nor the Hall pull, which is `position: fixed;
     bottom: 0` at z-index 200 and therefore paints OVER this screen (z-index
     60). On a laptop-height window the sixth card ran under it.
     Everything the rail has to clear is now named and subtracted, so six cards
     plus their gaps cannot overflow the space they are given.
     The card art is 222x296, so width = height * 0.75 everywhere it's needed. */
  --fs-card-gap: clamp(6px, 0.9vw, 14px);
  /* The hand sits along the bottom; each card is a share of the WIDTH (six
     across, gaps and padding subtracted, 296/222 art ratio), capped so it
     neither balloons on a wide screen nor eats a short one. Deriving height
     from the width share is what makes sideways overflow — the reason the old
     bottom row was once retired — impossible at any window size. */
  --fs-card-h: min(
    168px,
    24vh,
    calc((100vw - 20px - 5 * var(--fs-card-gap)) / 6 * 1.333)
  );
}
/* keeps the fighters readable against the lamps + wet cobbles */
.fs-scrim {
  position: absolute; inset: 0; pointer-events: none;
  background:
    radial-gradient(120% 80% at 50% 40%, transparent 40%, rgba(6, 4, 10, 0.55)),
    linear-gradient(180deg, rgba(6, 4, 10, 0.72) 0%, transparent 26%, transparent 52%, rgba(6, 4, 10, 0.88) 100%);
}

/* ── HUD ── */
.fs-hud {
  position: relative; z-index: 2;
  display: grid; grid-template-columns: 1fr auto 1fr auto;
  align-items: start; gap: 12px;
  padding: 14px clamp(10px, 2vw, 26px) 0;
}
/* Ornate art-deco HP bars: ONE generated gold frame (no portrait circle, no
   scratch texture — player feedback) shared by both sides, guest mirrored. The
   frame's interior groove is empty in the art; `.fs-groove` sits exactly in it
   (measured insets, see art.ts HP_BAR.groove) and holds the plain CSS fill that
   does the actual depleting — red for the hero, steel-blue for the enemy, each
   draining away from its own fighter, Tekken style. Count reads off the bar. */
.fs-bar {
  position: relative; display: flex; flex-direction: column; gap: 4px; max-width: 500px;
  /* A flex item's default min-width is `auto` (its content's min-content size),
     not 0 — without this override an unusually long enemy name (a name plate
     is arbitrary player/mob text, unbounded) can force this grid column WIDER
     than the space actually available, instead of letting .fs-name's own
     ellipsis (below) do its job. See the mobile .fs-track fix for the OTHER,
     bigger version of this same class of bug. */
  min-width: 0;
}
.fs-bar-home { margin-right: auto; align-items: flex-start; }
.fs-bar-guest { margin-left: auto; align-items: flex-end; }
.fs-name {
  font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.08em;
  text-transform: uppercase; color: var(--color-gold); padding: 0 4px;
  /* Never let a long name (either fighter's) push wider than its own bar —
     truncate instead of overflowing into (or past) the opposite side. */
  display: block; max-width: 100%; min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.fs-track {
  /* the frame art's 1414/140 ratio no longer governs anything — the channel is
     CSS, so it can be whatever height reads at this size */
  position: relative; width: min(42vw, 480px);
  padding: 5px 7px;
  background: var(--tex-forge), var(--mat-iron);
  background-size: 150px 150px, auto;
  background-blend-mode: soft-light, normal;
  clip-path: var(--clip-hewn);
  box-shadow: var(--rim-ice), 0 3px 10px rgba(0, 0, 0, 0.6);
}
/* the cord chased along the channel's edges, mirrored on the guest side so the two
   bars face each other */
.fs-track::after {
  content: ''; position: absolute; inset: 0; z-index: 3; pointer-events: none;
  opacity: 0.5;
  background:
    var(--orn-cord-dark) repeat-x left top 1px,
    var(--orn-cord-lite) repeat-x left top 2px,
    var(--orn-cord-dark) repeat-x left bottom 2px,
    var(--orn-cord-lite) repeat-x left bottom 1px;
}
.fs-track.is-guest { transform: scaleX(-1); }
/* ...and the numbers must not read backwards inside a mirrored box */
.fs-track.is-guest .fs-hp { transform: scaleX(-1); }
.fs-track .bar-shield { height: 20px; }
/* the count, read straight off the bar (above the frame overhang) */
.fs-hp {
  position: absolute; inset: 0; z-index: 3;
  display: grid; place-items: center;
  font-family: var(--font-heading, serif); font-variant-numeric: tabular-nums;
  font-size: clamp(12px, 1.5vh, 15px); letter-spacing: 0.02em;
  color: #fff; text-shadow: 0 1px 2px rgba(0, 0, 0, 0.95), 0 0 6px rgba(0, 0, 0, 0.8);
  pointer-events: none;
}

.fs-clock {
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  padding: 6px 18px; min-width: 108px;
  background: linear-gradient(180deg, rgba(20, 16, 12, 0.9), rgba(8, 6, 5, 0.95));
  border: 1px solid color-mix(in srgb, var(--color-gold) 40%, transparent);
  clip-path: polygon(7px 0, calc(100% - 7px) 0, 100% 7px, 100% calc(100% - 7px),
                     calc(100% - 7px) 100%, 7px 100%, 0 calc(100% - 7px), 0 7px);
}
.fs-clock-t { font-family: var(--font-heading, serif); font-size: 22px; line-height: 1; color: var(--color-gold-bright); }
.fs-clock-r { font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--color-text-dim); }

.fs-bail { align-self: start; }

/* ── Fighters ── */
.fs-stage {
  position: relative; z-index: 1; flex: 1;
  /* Floor, not 0: on a short viewport (landscape phone, small window) the
     outcome panel used to flex the stage down to a ~30px sliver — the winner's
     sprite read as cut out of the fight (QA 18.08 item 5). The panel scrolls
     instead (see .fs-outcome). */
  min-height: clamp(96px, 24vh, 240px);
  display: flex; align-items: flex-end; justify-content: space-between;
  /* the hand lives BELOW the stage now — the fighters get the full width */
  padding: 0 clamp(8px, 5vw, 90px);
}
.fs-fighter { position: relative; height: 78%; display: flex; align-items: flex-end; }
.fs-fighter img { height: 100%; width: auto; object-fit: contain; filter: drop-shadow(0 18px 26px rgba(0, 0, 0, 0.75)); }
.fs-enemy img { transform: scaleX(-1); }
/* mobs have no pose set — a portrait, framed so it reads as deliberate */
/* Mob art is a transparent CUTOUT like everything else now (the mythics were
   the last rectangles — regenerated 01.09, owner: "фон не вырезан"), so the
   old framed-photo treatment (gold border + object-fit cover) would draw an
   empty box around a figure. The mob stands on the ground like a fighter. */
.fs-enemy.is-mob img {
  height: 70%; align-self: flex-end;
  transform: none; object-fit: contain; object-position: bottom;
}
/* A mythic towers: the boss's box outgrows the fighter box, so even after
   poseFit normalizes the silhouette it stands a head above the hero. */
.fs-enemy.is-boss { height: 96%; }
/* A hold's elite beast fights posed in its OWN frames (fight/<slug>/, shot
   02.09) just under the world bosses' stature — the container override does
   what is-boss does. */
.fs-enemy.is-elite { height: 90%; }
/* fallback if an elite ever loses its pose set: static art at boss scale */
.fs-enemy.is-elite.is-mob img { height: 96%; }

/* Crossfade between pose stills — the leaving frame is taken out of flow so the
   two overlap instead of the box collapsing mid-swap. */
.fs-pose-enter-active, .fs-pose-leave-active { transition: opacity 260ms ease; }
.fs-pose-enter-from, .fs-pose-leave-to { opacity: 0; }
.fs-pose-leave-active { position: absolute; bottom: 0; }

.fs-pops { position: absolute; inset: 0; pointer-events: none; }
.fs-pop {
  position: absolute; top: 18%; left: 50%;
  font-family: var(--font-heading, serif); font-size: 28px; font-weight: 700;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.9);
  /* rise fast, then HOLD readable, then fade — keep in step with POP_MS */
  animation: fs-pop 2s cubic-bezier(0.16, 0.8, 0.4, 1) forwards;
}
.fs-pop.is-dmg { color: #ff7a6a; }
.fs-pop.is-crit { color: #ffd66a; font-size: 34px; }
.fs-pop.is-miss { color: #d8cfba; font-size: 21px; letter-spacing: 0.08em; }
.fs-pop.is-heal { color: #7fd08a; }
@keyframes fs-pop {
  0%   { transform: translate(-50%, 0); opacity: 0; }
  8%   { transform: translate(-50%, -18px); opacity: 1; }
  72%  { transform: translate(-50%, -44px); opacity: 1; }
  100% { transform: translate(-50%, -64px); opacity: 0; }
}

.fs-error {
  position: absolute; bottom: 30%; left: 50%; transform: translateX(-50%); z-index: 5;
  padding: 8px 14px; background: rgba(10, 6, 6, 0.9);
  border: 1px solid color-mix(in srgb, var(--color-error) 50%, transparent);
  color: var(--color-error); font-size: 13px;
}

/* ── Outcome ── */
.fs-outcome {
  position: relative; z-index: 3; margin: 0 auto 26px; padding: 20px 34px;
  display: flex; flex-direction: column; align-items: center; gap: 14px;
  background: #0c0f14;
  border: 1px solid var(--color-border); border-radius: 0;
  /* The panel yields, the fighters don't: cap it and scroll its own content so
     the winner stays on stage even on a landscape phone (QA 18.08 item 5). */
  max-height: min(62vh, 560px);
  overflow-y: auto;
}
/* Outcome: the take is shown as iconed brass plates, the loot as the real piece
   framed in its rarity. No banner art — it was visual overload on top of the
   screen's own backdrop. */
.fs-oc-levelup {
  font-family: var(--font-label, sans-serif); font-size: 11px; letter-spacing: 0.08em;
  text-transform: uppercase; color: var(--color-gold-bright);
}
.fs-plates { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; }
.fs-plate {
  display: flex; align-items: center; gap: 9px; padding: 8px 14px; text-align: left;
  background: #0c0f14; /* == the icons' own baked backdrop, so they dissolve into it */
  border: 1px solid color-mix(in srgb, var(--color-gold) 30%, transparent);
  clip-path: polygon(5px 0, calc(100% - 5px) 0, 100% 5px, 100% calc(100% - 5px),
                     calc(100% - 5px) 100%, 5px 100%, 0 calc(100% - 5px), 0 5px);
}
.fs-plate-art { width: 26px; height: 26px; object-fit: contain; flex: none; }
.fs-badge-elo { display: grid; place-items: center; font-size: 15px; }
.fs-badge-elo.is-win { color: var(--color-gold-bright); }
.fs-badge-elo.is-loss { color: #d98a8a; }
.fs-plate-label {
  font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.08em;
  text-transform: uppercase; color: var(--color-text-dim);
}
.fs-plate-num { font-family: var(--font-heading, serif); font-size: 17px; color: var(--color-gold); line-height: 1.2; }
.fs-plate-num.is-loss { color: #d98a8a; }
.fs-plate-num.is-xp { color: #9fd8a8; }
.fs-plate-sub { font-family: var(--font-body, serif); font-size: 11px; color: var(--color-text-dim); }
.fs-loot {
  display: flex; align-items: center; gap: 12px; padding: 10px 16px; text-align: left;
  border: 1px solid color-mix(in srgb, var(--rar, #888) 55%, transparent);
  background: #0c0f14; /* rarity stays on the border + glow; a wash here would
                          re-expose the icon's own square */
  box-shadow: 0 0 18px color-mix(in srgb, var(--rar, #888) 22%, transparent);
}
.fs-loot-art { width: 46px; height: 46px; flex: none; }
.fs-loot-art img { width: 100%; height: 100%; object-fit: contain; }
.fs-loot-name { font-family: var(--font-heading, serif); font-size: 16px; color: var(--rar, var(--color-gold)); }
.fs-rarity {
  font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.08em;
  text-transform: uppercase; color: var(--rar, var(--color-text-dim));
}
.fs-next { font-family: var(--font-body, serif); font-size: 14px; color: var(--color-text-dim); text-align: center; }
.fs-next-dim { font-size: 13px; font-style: italic; opacity: 0.8; margin-top: -4px; }
.fs-next-row { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; }
.fs-clock-w, .fs-clock-b {
  font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.07em;
  text-transform: uppercase; color: var(--color-gold);
}
.fs-clock-x {
  font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.07em;
  text-transform: uppercase; color: #e8907f;
  animation: fs-low 1.2s ease-in-out infinite;
}
.fs-clock-b { color: #9fd8a8; }

/* ── DUEL PACING ─────────────────────────────────────────────────────────
   The clock on your turn and the foe's deliberation tell. Both exist only in
   the Holmgang; a mob still trades blows at speed. */
.fs-turn {
  display: inline-flex; align-items: center; gap: 6px;
  font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.07em;
  text-transform: uppercase; color: #b6c6d6;
}
.fs-turn-n { font-weight: 400; color: var(--color-ice, #cfe8ff); }
.fs-turn-track {
  display: block; width: 56px; height: 3px;
  background: rgba(0, 0, 0, 0.55);
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}
.fs-turn-track i {
  display: block; height: 100%;
  background: var(--color-ice-dim, #7fb4d8);
  transition: width 900ms linear;
}
.fs-turn.is-late { color: #e8b0ba; }
.fs-turn.is-late .fs-turn-n { color: #f4ccd4; }
.fs-turn.is-late .fs-turn-track i { background: var(--color-error, #b3263a); }

.fs-think {
  position: absolute; left: 50%; bottom: 4px; transform: translateX(-50%);
  display: inline-flex; align-items: center; gap: 8px;
  padding: 5px 12px; white-space: nowrap;
  background: linear-gradient(180deg, rgba(6, 10, 18, 0.72), rgba(6, 10, 18, 0.92));
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.55), 0 4px 14px rgba(0, 0, 0, 0.5);
  font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.08em;
  text-transform: uppercase; color: #cfdae8;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.95);
  pointer-events: none;
}
.fs-think-dots { display: inline-flex; gap: 3px; }
.fs-think-dots i {
  width: 4px; height: 4px; border-radius: 50%;
  background: var(--color-ice-dim, #7fb4d8);
  animation: fs-think-pulse 1.1s ease-in-out infinite;
}
.fs-think-dots i:nth-child(2) { animation-delay: 0.18s; }
.fs-think-dots i:nth-child(3) { animation-delay: 0.36s; }
@keyframes fs-think-pulse {
  0%, 100% { opacity: 0.25; transform: translateY(0); }
  50%      { opacity: 1;    transform: translateY(-2px); }
}
@media (prefers-reduced-motion: reduce) {
  .fs-think-dots i { animation: none; opacity: 0.8; }
  .fs-turn-track i { transition: none; }
}
.fs-relief-enter-active, .fs-relief-leave-active { transition: opacity 220ms ease, transform 220ms ease; }
.fs-relief-enter-from, .fs-relief-leave-to { opacity: 0; transform: translate(-50%, calc(-50% + 8px)); }

/* the garrison hand-over card — carved, centred over the ring, gone in ~1.6s */
.fs-relief {
  position: absolute;
  top: 46%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 30;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 16px 26px;
  text-align: center;
  background: linear-gradient(180deg, rgba(8, 12, 20, 0.86), rgba(6, 9, 15, 0.95));
  box-shadow:
    inset 0 1px 0 rgba(207, 232, 255, 0.14),
    inset 0 0 0 1px rgba(0, 0, 0, 0.6),
    0 10px 34px rgba(0, 0, 0, 0.6);
  pointer-events: none;
}
.fs-relief-kicker {
  font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.1em;
  text-transform: uppercase; color: #9aabbd;
}
.fs-relief-name {
  font-family: var(--font-heading, serif); font-size: 19px; line-height: 1.15;
  color: var(--color-gold); text-shadow: 0 2px 6px rgba(0, 0, 0, 0.9);
}
.fs-relief-count {
  font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.08em;
  text-transform: uppercase; color: #7f93a8;
}

.fs-outcome h2 { font-family: var(--font-heading, serif); font-size: 26px; color: var(--color-gold); text-transform: uppercase; letter-spacing: 0.04em; }

/* ── Cards ──
   The hand is a HORIZONTAL ROW along the bottom, back where it was of old
   (owner's call — the left rail read badly and rode over the fighters). The
   reason the bottom row was once retired was sideways overflow on narrow
   windows; --fs-card-h now derives each card from a width share, so six always
   fit BY CONSTRUCTION and the row needs no scrolling at any size. */
.fs-cards {
  position: relative; z-index: 3;
  display: flex; justify-content: center; align-items: flex-end;
  gap: var(--fs-card-gap);
  padding: 6px 10px calc(8px + env(safe-area-inset-bottom, 0px));
}
.fs-card {
  position: relative; flex: 0 0 auto; min-height: 0; height: var(--fs-card-h);
  padding: 0; border: 0; background: transparent;
  cursor: pointer; line-height: 0;
  transition: transform 160ms ease, filter 160ms ease;
  transform-origin: bottom center;
}
.fs-card > img { height: 100%; width: auto; display: block; object-fit: contain; }
/* Hover ENLARGES the card 2x out of the row (QA 15.08 asked for the icon to
   grow 2x on hover — the baked-in card text was too small to read) and
   reveals the move's stat plate. Coarse pointers get a gentler 1.35x on
   press — a phone tray card at 2x would bury its neighbors. */
.fs-card:not(.is-off):hover {
  transform: translateY(-10px) scale(2); z-index: 5;
  filter: drop-shadow(0 10px 20px rgba(233, 193, 102, 0.3));
}
.fs-card:not(.is-off):active { transform: translateY(-4px) scale(2); z-index: 5; }
@media (pointer: coarse) {
  .fs-card:not(.is-off):hover, .fs-card:not(.is-off):active { transform: translateY(-6px) scale(1.35); }
}
.fs-card.is-off { cursor: not-allowed; filter: grayscale(0.85) brightness(0.45); }
/* The move's real numbers — hidden until the card enlarges. Sized small on
   purpose: it rides the card's own 2x scale up to readable. */
.fs-card-stats {
  display: none;
  position: absolute; left: 50%; bottom: 101%; transform: translateX(-50%);
  width: max-content; max-width: 30ch;
  padding: 2px 6px; text-align: center; white-space: normal;
  background: rgba(8, 6, 5, 0.94); border: 1px solid color-mix(in srgb, var(--color-gold) 45%, transparent);
  font-family: var(--font-label, sans-serif); font-size: 7px; letter-spacing: 0.05em;
  color: var(--color-parchment, #e8dcc0); line-height: 1.5;
}
.fs-card:not(.is-off):hover .fs-card-stats,
.fs-card:not(.is-off):active .fs-card-stats,
.fs-card:focus-visible .fs-card-stats { display: block; }
/* The Horn of Mead — same plate grammar as .fs-badge, but in the HUD flow
   (under the hero's HP boards), not floated over a card. */
.fs-horn {
  margin-top: 4px;
  display: inline-flex; align-items: center; gap: 3px;
  padding: 2px 7px; white-space: nowrap;
  background: rgba(8, 6, 5, 0.9); border: 1px solid color-mix(in srgb, var(--color-gold) 45%, transparent);
  font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.08em;
  color: var(--color-gold); line-height: 1.4;
  cursor: pointer;
}
.fs-horn:disabled { cursor: not-allowed; filter: grayscale(0.7) brightness(0.6); }
.fs-horn.is-spent { opacity: 0.55; }

/* the vigor tally — same chip language as the horn beside it */
/* Vigor as a SMALL HP-style gauge (owner 01.09), not a text chip — the same
   shield-board grammar as the health track above it, one board per point. */
.fs-vigor {
  margin-top: 4px;
  display: inline-flex; align-items: center; gap: 5px;
  white-space: nowrap;
  color: var(--color-aurora);
}
.fs-vigor-bar { width: 110px; height: 8px; flex: none; }
.fs-vigor-n {
  font-family: var(--font-label, sans-serif); font-size: 9px;
  letter-spacing: 0.08em; line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.85);
}
.fs-bolt { width: 10px; height: 10px; flex: none; }

.fs-badge {
  position: absolute; left: 50%; bottom: 6%; transform: translateX(-50%);
  padding: 2px 7px; white-space: nowrap;
  display: inline-flex; align-items: center; gap: 3px;
  background: rgba(8, 6, 5, 0.9); border: 1px solid color-mix(in srgb, var(--color-gold) 45%, transparent);
  font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.08em;
  color: var(--color-gold); line-height: 1.4;
}
/* can't afford the patch-up: the card is already greyed by is-off, this tints
   the price red so the reason is legible rather than a mystery */
.fs-badge.is-broke { color: #e8907f; border-color: color-mix(in srgb, var(--color-error) 55%, transparent); }
/* PATCH UP charges CASH, not gold bars (QA assumed gold since the shop's
   patch-up costs bars) — an explicit icon next to the number removes the
   ambiguity instead of a bare number. */
.fs-badge-cash { width: 9px; height: 9px; object-fit: contain; flex: none; }
.fs-key {
  position: absolute; top: 4%; right: 6%;
  font-family: var(--font-label, sans-serif); font-size: 9px; color: var(--color-text-dim);
  opacity: 0.6;
}

/* ── Phone ──────────────────────────────────────────────────────────────────
   At 390px the desktop layout simply does not fit: the two bars alone are 383px
   (before the clock or Walk-out), and six cards at their desktop height come to
   ~624px. So on a phone the cards are sized from the VIEWPORT — six always fit
   across, no sideways scroll — and the HUD drops to two rows so the bars keep
   their ratio instead of being squashed. */
/* The side rail is only worth its keep when the window can actually pay for it.
   It costs a fifth of the width AND needs ~950px of height to show six cards at
   a legible size — below that the cards shrink until "STANDARD ATTACK" renders
   at four pixels and the rail is a column of unreadable thumbnails. 700px was
   far too low a bar: at 900x900 the layout was technically not clipped and
   practically unusable. The tray now takes over whenever either dimension
   cannot carry the rail. */
@media (max-width: 900px) {
  .fs-hud {
    grid-template-columns: 1fr auto 1fr;   /* Walk-out leaves the row… */
    gap: 6px; padding: 8px 8px 0;
  }
  /* BUG (QA): `min(46vw, 240px)` was sized as a fraction of the FULL viewport,
     not of the space actually left after the clock column + gaps + hud
     padding — at 390px-wide that's 179px per side, and 2×179 + the clock
     (~64px) + 2 gaps (6px) + hud padding (16px) = ~451px, ~61px WIDER than
     the 390px screen. The overflow wasn't clipped evenly; the whole guest
     (enemy) column — bar, HP text, and the enemy's name plate — was shoved
     that far past the right edge, entirely off-screen (measured: the name's
     own box started at x=391 on a 390px viewport, i.e. 0% of it was visible).
     Fixed formula: solve directly for "half of what's left after the clock's
     floor width + gaps + padding" instead of a bare viewport fraction, so it
     fits BY CONSTRUCTION at any phone width, not just the one this got
     eyeballed against. A small clock-width margin (60 vs the clock's
     measured ~64px) absorbs a wider clock (heist "Banked X" / wave text can
     run longer than the plain timer); max(84px, …) keeps the bar from
     collapsing to nothing on the very narrowest phones. */
  .fs-track { width: max(84px, min(calc(50vw - 60px), 240px)); }
  .fs-name { font-size: 8px; }
  .fs-hp { font-size: 11px; }
  .fs-clock { min-width: 64px; padding: 3px 8px; }
  .fs-clock-t { font-size: 15px; }
  .fs-clock-r, .fs-clock-w, .fs-clock-b { font-size: 7px; letter-spacing: 0.07em; }
  /* …and becomes a compact plate pinned BELOW the hud band (the hud measures
     ~56px on a phone; 46px parked the button on top of the guest bar) */
  .fs-bail { position: absolute; top: 60px; right: 8px; z-index: 5; }

  .fs-stage { padding: 0 2px; }
  .fs-fighter { height: 66%; }
  /* the taller two-row card tray below eats the frame's bottom — lift the
     backdrop's focal point so the street scene rides above the cards, and the
     bottom-anchored fighters rise with the shorter stage (QA: "push the
     characters and the background higher") */
  .fs-root { background-position: center 28%; }

  /* QA: action cards were 6-across slivers (~60px). Now a 3×2 grid — each
     card roughly doubles; order stays 1-2-3 / 4-5-6 so PATCH UP ends
     bottom-right. justify-items centers the art in its cell. The desktop
     side-rail is opted OUT of here (position: static drops it back into the
     flex column as a bottom tray) — a vertical rail would eat a third of a
     phone's width. */
  .fs-cards {
    position: static;
    display: grid; grid-template-columns: repeat(3, 1fr);
    justify-items: center; align-items: end;
    /* the tray sits ON the bottom (owner's call — the expanded chat overlays
       the whole screen anyway, so the collapsed Hall tab may ride over the
       corner card); only the iOS home-indicator band is cleared */
    gap: 6px 4px;
    padding: 4px 8px calc(10px + env(safe-area-inset-bottom, 0px));
  }
  .fs-card { flex: none; width: 100%; display: flex; justify-content: center; }
  .fs-card > img { height: auto; width: min(100%, calc((100vw - 24px) / 3)); }
  .fs-card:not(.is-off):hover { transform: none; filter: none; }  /* no hover on touch */
  .fs-badge { font-size: 9px; padding: 2px 5px; bottom: 4%; gap: 3px; }
  .fs-badge-cash { width: 9px; height: 9px; }
  .fs-key { display: none; }                /* no keyboard to hint at */

  .fs-outcome { margin: 0 8px 10px; padding: 14px 16px; }
  .fs-plate { padding: 6px 10px; gap: 6px; }
  .fs-plate-num { font-size: 14px; }
  .fs-plate-art { width: 20px; height: 20px; }
}

/* Landscape phone: almost no height. Give it to the fighters. */
@media (max-height: 480px) and (orientation: landscape) {
  .fs-track { width: min(30vw, 200px); }
  .fs-fighter { height: 72%; }
}
@media (prefers-reduced-motion: reduce) {
  .fs-card, .fs-fill { transition: none; }
  .fs-fill.is-low { animation: none; }
  .fs-pose-enter-active, .fs-pose-leave-active { transition: none; }
  .fs-pop { animation-duration: 0.01ms; }
}
</style>
